/**
 * useExtraHoursFlow — la machine de « Réserver un cours en plus » → les props de
 * `<ExtraHoursFlow>`.
 *
 * Un seul envoi, celui de `ParentExtraHoursModal.js` : `createExtraHourRequest`
 * (`POST /api/v1/extra-hour-requests`, `:146`), avec le corps de `transformSubmitData`
 * (`:69-103`) — `preferred_slots` en cours partagé, `requested_dates` en individuel. Quand la
 * demande a pris l'heure du mois de l'offre Autonomie, l'élève est relu après l'envoi.
 *
 * Quand et combien la demande se facture se lit sur l'élève (`can_use_next_invoice`,
 * `autonomy_hour_available`), par `extraHoursBilling` — la règle de la note du ticket.
 *
 * Les catalogues, les seuils et les phrases de refus vivent dans
 * `adapters/parent/ExtraHours/extraHoursAdapter` ; le gabarit ne juge que la complétude d'une
 * étape. Deux normalisations du legacy sont remontées dans le réducteur, où elles se testent :
 * la purge des créneaux d'un jour devenu inatteignable quand l'échéance se resserre
 * (`SharedSlotsStep.js:41-52`) et l'alignement des lignes de dates sur le nombre d'heures
 * (`IndividualDatesStep.js:50-60`).
 *
 * **Un seul parcours tant qu'un envoi est en vol**, comme le rattrapage (`useRecoverAbsenceFlow`).
 * Fermer pendant l'envoi ne fait que CACHER la feuille : la demande est partie, sa réponse doit
 * encore être lue (accusé, relecture de l'élève, `onSuccess`). La rouvrir avant la réponse la
 * remontre telle quelle, bouton en chargement — un parcours neuf laisserait partir une seconde
 * demande. À la réponse, feuille visible : l'accusé ou le refus s'écrivent dans l'étape ; feuille
 * cachée : le bandeau de l'écran le dit (`notice`). Un succès remet alors le parcours à zéro ; un
 * refus le garde, pour que la feuille rouverte le montre dans son étape, brouillon intact. La
 * fermeture n'est jamais verrouillée : une requête sans réponse laisserait une feuille infermable.
 *
 * ⚠️ `student` est l'élève affiché (`selectDisplayedStudent`) : `connect` écrasait déjà la prop `user` de la modale
 * legacy (`:170`). En mode parent, c'est l'enfant affiché. Voir `TRANSITION.md` § Heures
 * supplémentaires.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef } from "react"
import moment from "moment"
import { useDispatch, useSelector } from "react-redux"

import { apiIdle, apiRequest } from "../../../../data/redux/actions/api"
import { authService } from "../../../../data/services/authService"
import { subscriptionService } from "../../../../data/services/subscriptionService"
import { type SnackbarProps, type SnackbarTone } from "../../../../design-system/molecules/Snackbar"
import {
    type ExtraHoursFlowProps,
    type ExtraHoursStep,
} from "../../../../design-system/templates/ExtraHoursFlow/ExtraHoursFlow"
import {
    alignRequestedDates,
    computeAllowedDays,
    EXTRA_HOURS_DEADLINE_OPTIONS,
    EXTRA_HOURS_HOUR_OPTIONS,
    extraHoursBilling,
    extraHoursDateBounds,
    extraHoursRequestBody,
    extraHoursStepComplete,
    extraHoursTimezoneNotice,
    extraHoursTotalLabel,
    initialExtraHoursDraft,
    purgeSlotIdsOutOfWindow,
    toExtraHoursDays,
    toExtraHoursFormatOptions,
    toExtraHoursSubjects,
    validateExtraHoursDraft,
    type ApiExtraHoursBillingFacts,
    type ApiExtraHoursStudent,
} from "../../adapters/parent/ExtraHours/extraHoursAdapter"
import { type ApiSchoolSubject } from "../../adapters/parent/shared/schoolSubjectAdapter"
import { childDisplayName } from "../formatters/childFormatter"
import { type UIExtraHoursDraft } from "../types"
import { apiErrorMessage } from "./apiErrorMessage"
import { useGesture } from "./gestureTracking"
import { selectDisplayedStudent } from "../../view-models/shared/displayedStudent"

/**
 * Le repli quand le serveur refuse sans dire pourquoi. Le message du back prime : il porte la
 * raison du 422. Déclaré ici plutôt qu'importé du design system — une VALEUR du design system
 * embarque Unistyles, que Jest ne sait pas charger.
 */
const EXTRA_HOURS_SUBMIT_ERROR_FALLBACK =
    "Votre demande n'a pas pu être envoyée. Vérifiez vos informations et réessayez."
/** Ce que le bandeau dit quand la feuille était cachée à la réponse — le titre de l'accusé. */
const NOTICE_SENT = "Demande envoyée"

/** Référence stable : un `?? []` dans un sélecteur rendrait un tableau neuf à chaque lecture. */
const NO_SUBJECTS: ApiSchoolSubject[] = []

/** Les deux catalogues d'une feuille fermée — mêmes références d'un rendu à l'autre. */
const NO_SUBJECT_SPLIT: ReturnType<typeof toExtraHoursSubjects> = { subjects: [], otherSubjects: [] }
const NO_FORMAT_OPTIONS: ReturnType<typeof toExtraHoursFormatOptions> = []

export type ExtraHoursFlowState = {
    visible: boolean
    step: ExtraHoursStep
    draft: UIExtraHoursDraft
    /** Vrai de l'envoi à sa réponse : fermer ne fait alors que cacher la feuille. */
    submitting: boolean
    submitError: string | null
    /** Le bandeau de l'écran — l'issue d'un envoi dont la feuille était cachée. */
    notice: { message: string; tone: SnackbarTone } | null
    /** L'élève du parcours, pris à l'ouverture : un refus gardé ne se remontre qu'à lui. */
    studentId: string | number | null
}

export const initialExtraHoursFlowState: ExtraHoursFlowState = {
    visible: false,
    step: "parameters",
    draft: initialExtraHoursDraft(),
    submitting: false,
    submitError: null,
    notice: null,
    studentId: null,
}

export type ExtraHoursFlowAction =
    | { type: "OPEN"; studentId?: string | number | null }
    | { type: "CLOSE" }
    | { type: "CHANGE"; draft: UIExtraHoursDraft; allowedDays: Set<string> | null }
    | { type: "CONTINUE" }
    | { type: "BACK" }
    | { type: "SUBMIT" }
    | { type: "SUBMIT_SUCCESS" }
    | { type: "SUBMIT_ERROR"; message: string }
    | { type: "DISMISS_NOTICE" }

/** Un parcours remis à zéro : seul le bandeau survit, il parle d'un envoi déjà joué. */
function resetFlow(state: ExtraHoursFlowState): ExtraHoursFlowState {
    return { ...initialExtraHoursFlowState, draft: initialExtraHoursDraft(), notice: state.notice }
}

/** La deuxième étape a deux visages : le format décide lequel (`ParentExtraHoursModal.js:24-31`). */
function slotStepFor(draft: UIExtraHoursDraft): ExtraHoursStep {
    return draft.format === "individual" ? "dates" : "slots"
}

export function extraHoursFlowReducer(state: ExtraHoursFlowState, action: ExtraHoursFlowAction): ExtraHoursFlowState {
    switch (action.type) {
        case "OPEN": {
            const studentId = action.studentId ?? null
            // Un envoi en vol : c'est SON parcours qu'on remontre, tel quel. Un refus arrivé feuille
            // cachée aussi — il se lit dans son étape —, tant que c'est le même élève qui rouvre.
            const refusalKept = !state.visible && state.submitError != null && state.studentId === studentId
            if (state.submitting || refusalKept) return { ...state, visible: true }
            // Le legacy repart d'un brouillon neuf à chaque ouverture (`:62-71`) : les
            // disponibilités d'une demande précédente ne présument pas de celle-ci.
            return { ...resetFlow(state), visible: true, studentId }
        }

        case "CLOSE":
            // Pendant l'envoi, la feuille se cache seulement : la réponse doit retrouver son parcours.
            if (state.submitting) return { ...state, visible: false }
            return resetFlow(state)

        case "CHANGE": {
            const draft = action.draft
            return {
                ...state,
                draft: {
                    ...draft,
                    slotIds: purgeSlotIdsOutOfWindow(draft.slotIds, action.allowedDays),
                    requestedDates:
                        draft.format === "individual"
                            ? alignRequestedDates(draft.requestedDates, draft.hours)
                            : draft.requestedDates,
                },
            }
        }

        case "CONTINUE":
            if (state.step === "parameters") return { ...state, step: slotStepFor(state.draft) }
            if (state.step === "slots" || state.step === "dates") return { ...state, step: "recap" }
            return state

        case "BACK":
            if (state.step === "recap") return { ...state, step: slotStepFor(state.draft), submitError: null }
            if (state.step === "slots" || state.step === "dates") return { ...state, step: "parameters" }
            return state

        case "SUBMIT":
            // La demande ne part que de la validation de l'achat : c'est là que le parent voit le
            // prix et dit « Confirmer l'achat ».
            if (state.step !== "recap" || state.submitting) return state
            return { ...state, submitting: true, submitError: null }

        case "SUBMIT_SUCCESS":
            if (!state.visible) return { ...resetFlow(state), notice: { message: NOTICE_SENT, tone: "success" } }
            return { ...state, submitting: false, submitError: null, step: "done" }

        case "SUBMIT_ERROR": {
            // Feuille visible ou cachée, le refus reste dans son étape avec le brouillon : rien
            // n'est perdu. Cachée, le bandeau le dit en plus — sinon le parent croirait sa demande partie.
            const refused = { ...state, submitting: false, submitError: action.message }
            return state.visible ? refused : { ...refused, notice: { message: action.message, tone: "danger" } }
        }

        case "DISMISS_NOTICE":
            return state.notice == null ? state : { ...state, notice: null }

        default:
            return state
    }
}

type ApiSlice = { request?: boolean; success?: boolean; error?: unknown }

export type ExtraHoursFlowConfig = {
    /** Prénom de l'enfant — l'écran le connaît déjà, il évite une seconde lecture du store. */
    childFirstname?: string | null
    /** Rejoué au succès, pour l'écran qui veut recharger ce que la demande change. */
    onSuccess?: () => void
    /** Place à réserver sous le bandeau d'accusé — la hauteur de la barre d'onglets. */
    snackbarBottomOffset?: number
}

export type ExtraHoursFlowAdapter = {
    /** Ouvre la feuille sur un brouillon neuf. */
    open: () => void
    /** Les props de `<ExtraHoursFlow>`, prêtes à être étalées. */
    flowProps: ExtraHoursFlowProps
    /** Le bandeau de l'écran : l'issue d'un envoi dont la feuille était cachée à la réponse. */
    notice: SnackbarProps
}

export function useExtraHoursFlow(config: ExtraHoursFlowConfig = {}): ExtraHoursFlowAdapter {
    const { childFirstname, onSuccess, snackbarBottomOffset = 0 } = config
    const dispatch = useDispatch()
    const [state, dispatchUi] = useReducer(extraHoursFlowReducer, initialExtraHoursFlowState)
    // Stable : le bandeau réarme son minuteur de retrait à chaque nouveau `onDismiss`.
    const dismissNotice = useCallback(() => dispatchUi({ type: "DISMISS_NOTICE" }), [])

    const priceGrid = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (s: any) => s?.data?.staticData?.extra_hour_price_per_hour as Record<string, number> | undefined
    )
    const serverTimezone = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (s: any) => s?.data?.staticData?.server_timezone as string | undefined
    )
    const schoolSubjects = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (s: any) => (s?.data?.formData?.schoolSubjects ?? NO_SUBJECTS) as ApiSchoolSubject[]
    )
    const student = useSelector(selectDisplayedStudent)
    const submitReq = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (s: any) => s?.api?.createExtraHourRequest as ApiSlice | undefined
    )

    const gesture = useGesture("extra_hours.request")
    const awaitingSubmitRef = useRef(false)
    // Lue à l'envoi, pas au retour : l'élève a pu être relu entre les deux.
    const tookAutonomyHourRef = useRef(false)
    const handledSubmitSuccessRef = useRef(false)
    const handledSubmitErrorRef = useRef(false)

    const studentId = (student?.id ?? null) as string | number | null

    const open = useCallback(() => {
        gesture.opened()
        dispatchUi({ type: "OPEN", studentId })
        // Un envoi en vol : ni ses gardes ni sa tranche ne sont touchées, sa réponse reste à lire.
        if (awaitingSubmitRef.current) return
        handledSubmitSuccessRef.current = false
        handledSubmitErrorRef.current = false
        // Un 422 laissé sur la tranche repeindrait le récapitulatif à la réouverture (`:66-70`).
        dispatch(apiIdle(subscriptionService.createExtraHourRequest) as never)
    }, [dispatch, studentId, gesture])

    // Fermer n'abandonne pas l'écoute : `awaitingSubmitRef` reste levé jusqu'à la réponse.
    const close = useCallback(() => dispatchUi({ type: "CLOSE" }), [])

    const change = useCallback((draft: UIExtraHoursDraft) => {
        dispatchUi({
            type: "CHANGE",
            draft,
            allowedDays: computeAllowedDays(draft.deadlineType, draft.customDeadline),
        })
    }, [])

    const { draft } = state

    const days = useMemo(
        () => toExtraHoursDays(computeAllowedDays(draft.deadlineType, draft.customDeadline)),
        [draft.deadlineType, draft.customDeadline]
    )

    // `student` change d'identité à chaque rechargement du compte : sans la garde sur `visible`, les
    // deux catalogues se reconstruiraient pour une feuille que personne ne regarde.
    const { subjects, otherSubjects } = useMemo(
        () =>
            state.visible ? toExtraHoursSubjects(schoolSubjects, student as ApiExtraHoursStudent) : NO_SUBJECT_SPLIT,
        [state.visible, schoolSubjects, student]
    )

    const bounds = useMemo(() => extraHoursDateBounds(moment()), [])

    const submit = useCallback(() => {
        // Même garde que le réducteur, côté appel : rien ne part avant « Confirmer l'achat », ni deux
        // fois — le ref tranche aussi le double appui, que `state.submitting` ne voit qu'au rendu suivant.
        if (state.step !== "recap" || state.submitting || awaitingSubmitRef.current) return
        awaitingSubmitRef.current = true
        handledSubmitSuccessRef.current = false
        handledSubmitErrorRef.current = false
        tookAutonomyHourRef.current =
            extraHoursBilling(draft, student as ApiExtraHoursBillingFacts).autonomyHour === "included"
        dispatchUi({ type: "SUBMIT" })
        dispatch(apiRequest(subscriptionService.createExtraHourRequest, {}, extraHoursRequestBody(draft)) as never)
        gesture.submitted()
    }, [dispatch, draft, student, state.step, state.submitting, gesture])

    useEffect(() => {
        if (!awaitingSubmitRef.current || !submitReq?.success || handledSubmitSuccessRef.current) return
        handledSubmitSuccessRef.current = true
        awaitingSubmitRef.current = false
        gesture.succeeded()
        dispatchUi({ type: "SUBMIT_SUCCESS" })
        dispatch(apiIdle(subscriptionService.createExtraHourRequest) as never)
        // La demande a pris l'heure du mois : on relit l'élève, sinon la suivante l'annoncerait
        // encore comprise. Aucune autre demande ne change ce que l'élève en dit.
        if (tookAutonomyHourRef.current) dispatch(apiRequest(authService.getUser) as never)
        onSuccess?.()
    }, [dispatch, onSuccess, submitReq?.success, gesture])

    useEffect(() => {
        if (!awaitingSubmitRef.current || !submitReq?.error || handledSubmitErrorRef.current) return
        handledSubmitErrorRef.current = true
        awaitingSubmitRef.current = false
        gesture.failed(submitReq.error)
        // La feuille reste ouverte : le brouillon n'est pas perdu, comme sur le pas d'étape legacy.
        dispatchUi({
            type: "SUBMIT_ERROR",
            message: apiErrorMessage(submitReq.error, EXTRA_HOURS_SUBMIT_ERROR_FALLBACK),
        })
        dispatch(apiIdle(subscriptionService.createExtraHourRequest) as never)
    }, [dispatch, submitReq?.error, gesture])

    useEffect(
        () => () => {
            dispatch(apiIdle(subscriptionService.createExtraHourRequest) as never)
        },
        [dispatch]
    )

    const name = childDisplayName(childFirstname, student)

    // Feuille fermée : `<Overlay>` ne rend rien, donc catalogues, bornes, total et validation
    // seraient composés pour personne — à chaque action du store, et deux fois, puisque l'accueil
    // et le portail montent tous les deux ce parcours.
    const notice: SnackbarProps = {
        visible: state.notice != null,
        message: state.notice?.message ?? "",
        tone: state.notice?.tone ?? "success",
        onDismiss: dismissNotice,
        bottomOffset: snackbarBottomOffset,
    }

    if (!state.visible) {
        return {
            open,
            notice,
            flowProps: {
                visible: false,
                onClose: close,
                step: state.step,
                childFirstname: name,
                draft,
                onChange: change,
                hourOptions: EXTRA_HOURS_HOUR_OPTIONS,
                formatOptions: NO_FORMAT_OPTIONS,
                deadlineOptions: EXTRA_HOURS_DEADLINE_OPTIONS,
                subjects: NO_SUBJECT_SPLIT.subjects,
                totalLabel: "",
                billing: "next_invoice",
                onContinue: () => dispatchUi({ type: "CONTINUE" }),
            },
        }
    }

    const billing = extraHoursBilling(draft, student as ApiExtraHoursBillingFacts)

    return {
        open,
        notice,
        flowProps: {
            visible: true,
            onClose: close,
            step: state.step,
            childFirstname: name,

            draft,
            onChange: change,

            hourOptions: EXTRA_HOURS_HOUR_OPTIONS,
            formatOptions: toExtraHoursFormatOptions(priceGrid),
            deadlineOptions: EXTRA_HOURS_DEADLINE_OPTIONS,
            subjects,
            otherSubjects,
            days,

            minDate: bounds.minDate,
            maxDate: bounds.maxDate,
            timezoneNotice: extraHoursTimezoneNotice(student?.timezone, serverTimezone),
            totalLabel: extraHoursTotalLabel(priceGrid, { format: draft.format, hours: billing.billedHours }),
            billing: billing.billing,
            recapKind: billing.recapKind,
            autonomyNote: billing.autonomyNote,

            onContinue: () => dispatchUi({ type: "CONTINUE" }),
            onBack: () => dispatchUi({ type: "BACK" }),
            onSubmit: submit,
            submitting: state.submitting,
            submitError: state.submitError ?? undefined,
            errors: validateExtraHoursDraft(draft),
            continueDisabled: !extraHoursStepComplete(draft, state.step),
        },
    }
}
